import loadable from '@loadable/component';
import { type FunctionComponent, type PropsWithChildren, type ReactElement, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ClickableElement } from '../../../../../components/buttons';
import { ProductMatchingProducts } from '../../../../../components/product-components/product-matching-products/product-matching-products.component';
import { ProductSpecs } from '../../../../../components/product-components/product-specs/product-specs.component';
import { StarRatingWithCountAndValue } from '../../../../../components/product-components/rating/rating.component';
import { RedirectWithStatus } from '../../../../../components/redirect-w-status';
import ChevronDownIcon from '../../../../../components/svgs/untitled-ui/chevron-down.svg';
import ChevronUpIcon from '../../../../../components/svgs/untitled-ui/chevron-up.svg';
import {
	MATCHING_PRODUCTS_SECTION_ID,
	OVERVIEW_SECTION_ID,
	PRODUCT_PARTS_SECTION_ID,
	PRODUCT_QA_SECTION_ID,
	PRODUCT_SPECS_SECTION_ID,
	REVIEWS_SECTION_ID
} from '../../../../../constants/product';
import { ReviewsContextProvider } from '../../../../../contexts/review/reviews.context';
import {
	buildGTMReviewsExpanded,
	buildGTMQAExpanded,
	buildGTMSpecsExpanded
} from '../../../../../helpers/analytics/gtm/product-event-builders';
import { buildContainerPageUrl } from '../../../../../helpers/product-helper/reviews-qa.helper';
import { useTrackEvent } from '../../../../../hooks/analytics/analytics.hooks';
import { useSiteViewPreference } from '../../../../../hooks/apollo/employee/employee.hooks';
import { useFeature } from '../../../../../hooks/features/features.hooks';
import { useRouterLocation } from '../../../../../hooks/routing/location.hook';
import { useProductStickyNavStore } from '../../../../../store/product-sticky-nav/product-sticky-nav.store';
import { type PdpProductFamily } from '../../../../../types/product.types';
import { ProductOverview } from '../product-overview/product-overview.component';

const TITLE_QA = 'Questions & Answers';
const TITLE_REVIEWS = 'Reviews';
const TITLE_MATCHING_PRODUCTS = 'Matching Products';
const TITLE_DETAILS = 'Details';
const TITLE_PARTS = 'Parts';
const TITLE_SPECS = 'Specifications';

const LoadableProductReviews = loadable(
	() =>
		import(
			/* webpackChunkName: "ProductReviews" */ '../../../../../components/product-components/product-reviews/product-reviews.component'
		),
	{
		resolveComponent: ({ ProductReviews }) => ProductReviews
	}
);

const LoadableProductParts = loadable(
	() =>
		import(/* webpackChunkName: "ProductParts" */ '../../../../../components/product-components/product-parts/product-parts.component'),
	{
		resolveComponent: ({ ProductParts }) => ProductParts
	}
);

const LoadableQuestionsAnswersContainer = loadable(
	() =>
		import(
			/* webpackChunkName: "QuestionsAnswersContainer" */ '../../../../../components/product-components/product-questions-and-answers/questions-answers-container/questions-answers-container.component'
		),
	{
		resolveComponent: ({ QuestionsAnswersContainer }) => QuestionsAnswersContainer
	}
);

type SectionHeaderProps = {
	title: string;
	isOpen: boolean | undefined;
	onClick: () => void;
	headerContent?: ReactElement;
	alwaysPrint?: boolean;
};

const SectionHeader: FunctionComponent<PropsWithChildren<SectionHeaderProps>> = ({
	title,
	isOpen,
	onClick,
	headerContent,
	alwaysPrint = false,
	children
}) => {
	const trackEvent = useTrackEvent();
	useEffect(() => {
		if (isOpen) {
			if (title === TITLE_REVIEWS) {
				trackEvent(buildGTMReviewsExpanded());
			} else if (title === TITLE_QA) {
				trackEvent(buildGTMQAExpanded());
			} else if (title === TITLE_SPECS) {
				trackEvent(buildGTMSpecsExpanded());
			}
		}
	}, [isOpen]);

	return (
		<>
			<ClickableElement onClick={onClick} ariaLabel={`${title} header`} className="hover-bg-theme-grey-lighter">
				<div className="flex flex-row">
					<div className="flex flex-column w-100">
						<div className="flex pl3">
							<h3 className="f3">{title}</h3>
							{headerContent && <div className="flex flex-column justify-center pl3 pt1">{headerContent}</div>}
						</div>
					</div>
					<div className="flex flex-column justify-center pr3">
						{isOpen ? <ChevronUpIcon className="text-4xl" /> : <ChevronDownIcon className="text-4xl" />}
					</div>
				</div>
			</ClickableElement>
			<div
				className={`pb5 ph3 bt b--theme-grey-light ${isOpen ? '' : 'hidden'} ${alwaysPrint ? 'print:block' : ''}`}
				data-testid={`${title.toLowerCase()}-section-content`}>
				{children}
			</div>
		</>
	);
};

type PdpSectionContainerProps = {
	title: string;
	id: string;
	className?: string;
	headerContent?: ReactElement;
	startOpen?: boolean;
	showHeader?: boolean;
	printIfOpen?: boolean;
	alwaysPrint?: boolean;
};

const SectionContainer: FunctionComponent<PropsWithChildren<PdpSectionContainerProps>> = ({
	title,
	id,
	className = '',
	startOpen,
	headerContent,
	showHeader = true,
	printIfOpen = false,
	alwaysPrint = false,
	children
}) => {
	const [isOpen, setIsOpen] = useState(startOpen);
	const { ref: sectionRef, inView } = useInView({ rootMargin: '-5% 0% -85% 0%' });
	const setCurrentActiveSection = useProductStickyNavStore((state) => state.setCurrentActiveSection);
	const sectionToOpen = useProductStickyNavStore((state) => state.sectionToOpen);
	const setSectionToOpen = useProductStickyNavStore((state) => state.setSectionToOpen);

	const shouldPrint = alwaysPrint || (printIfOpen && isOpen);

	useEffect(() => {
		if (inView) {
			setCurrentActiveSection(id);
		}
	}, [inView]);

	useEffect(() => {
		if (sectionToOpen === id) {
			setIsOpen(true);
			setSectionToOpen('');
		}
	}, [sectionToOpen]);

	const handleHeaderClick = () => {
		setIsOpen(!isOpen);
	};

	const renderContent = () => {
		// When open: render children normally (visible on screen and print)
		if (isOpen) {
			return children;
		}
		// When closed but alwaysPrint: render with hidden class (hidden on screen, visible on print)
		if (alwaysPrint) {
			return <div className="hidden print:block">{children}</div>;
		}
		// When closed and no alwaysPrint: don't render at all
		return null;
	};

	return (
		<section
			id={id}
			className={`mv2 ba br1 b--theme-grey-light ${className} ${shouldPrint ? '' : 'print:hidden'}`}
			data-testid={`pdp-${title.toLowerCase()}-section`}
			ref={sectionRef}>
			{showHeader ? (
				<SectionHeader
					title={title}
					isOpen={isOpen}
					onClick={handleHeaderClick}
					headerContent={headerContent}
					alwaysPrint={alwaysPrint}>
					{renderContent()}
				</SectionHeader>
			) : (
				renderContent()
			)}
		</section>
	);
};

export type ProductDetailsSectionProps = {
	product: PdpProductFamily;
	familyId: number;
};

export const ProductDetailsSection: FunctionComponent<ProductDetailsSectionProps> = ({ product, familyId }) => {
	const { ref: detailsRef } = useInView({ triggerOnce: true });
	const { ref: replacementPartsRef, inView: replacementPartsInView } = useInView({ triggerOnce: true });
	const { ref: matchingProductsRef, inView: matchingProductsInView } = useInView({ triggerOnce: true });

	const specsOpenByDefault = useFeature('pdp-specs-open-by-default', { isSiteWide: true });
	const luxSpecsSectionActive = useFeature('luxury-pdp-specs-section', { isSiteWide: true });
	const { showAsEmployee } = useSiteViewPreference();

	// If reviews=true is in the URL, redirect to the container page.
	const { search, hash } = useRouterLocation();
	const params = new URLSearchParams(search);
	const shouldRedirectToContainer = params.get('reviews') === 'true';
	const campaignId = params.get('campaign');
	const [hasPartsHash, setHasPartsHash] = useState(false);

	useEffect(() => {
		setHasPartsHash(hash === `#${PRODUCT_PARTS_SECTION_ID}`);
	}, [hash]);

	return !shouldRedirectToContainer ? (
		<section data-testid="product-details-section-sticky-nav" className="page-avoid-p w-100">
			<div ref={detailsRef}>
				<SectionContainer title={TITLE_DETAILS} id={OVERVIEW_SECTION_ID} startOpen={true} printIfOpen={true}>
					<ProductOverview product={product} />
				</SectionContainer>
			</div>
			{luxSpecsSectionActive && (product.resources?.length || product.attributeGroups?.length) && (
				<div>
					<SectionContainer
						title={TITLE_SPECS}
						id={PRODUCT_SPECS_SECTION_ID}
						alwaysPrint={true}
						startOpen={showAsEmployee || specsOpenByDefault}>
						<ProductSpecs product={product} />
					</SectionContainer>
				</div>
			)}
			<div>
				<SectionContainer
					title={TITLE_REVIEWS}
					id={REVIEWS_SECTION_ID}
					printIfOpen={true}
					headerContent={
						<StarRatingWithCountAndValue
							reviewCount={product.reviewSummary.reviewCount}
							ratingValue={product.reviewSummary.ratingValue}
						/>
					}>
					<ReviewsContextProvider familyId={familyId}>
						<LoadableProductReviews />
					</ReviewsContextProvider>
				</SectionContainer>
			</div>
			<div>
				<SectionContainer title={TITLE_QA} id={PRODUCT_QA_SECTION_ID} printIfOpen={true}>
					<LoadableQuestionsAnswersContainer />
				</SectionContainer>
			</div>
			<div ref={matchingProductsRef}>
				<SectionContainer title={TITLE_MATCHING_PRODUCTS} id={MATCHING_PRODUCTS_SECTION_ID} startOpen={true}>
					{(matchingProductsInView || hasPartsHash) && <ProductMatchingProducts product={product} />}
				</SectionContainer>
			</div>
			<div ref={replacementPartsRef}>
				{product.hasReplacementParts && (
					<SectionContainer title={TITLE_PARTS} id={PRODUCT_PARTS_SECTION_ID} startOpen={true}>
						{(replacementPartsInView || hasPartsHash) && <LoadableProductParts familyId={familyId} />}
					</SectionContainer>
				)}
			</div>
		</section>
	) : (
		<RedirectWithStatus to={buildContainerPageUrl({ campaignId: campaignId || '', productId: `cp-${String(product.id)}` })} />
	);
};
